import React from 'react';
import { getDashboardInvoiceTableData } from '../../../Actions/DashboardActions';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import NoData from '../../../components/NoData';

const MyInvoices = ({ user }) => {
  const navigate = useNavigate();
  const {
    data: invoicesData,
    isLoading: invoicesDataLoading,
    isError: invoicesDataError,
  } = useQuery({
    queryFn: () => getDashboardInvoiceTableData(user?.organizationId),
    queryKey: ['dashboardInvoices', user?.organizationId],
    enabled: !!user?.organizationId,
    staleTime: 10000,
    cacheTime: 15000,
  });

  return (
    <div className="w-full bg-white border border-[#E0E0E0] rounded-xl h-[360px] flex flex-col overflow-hidden">
      <div className="px-6 py-4 border-b border-[#E0E0E0] flex items-center justify-between bg-gray-50/50">
        <div>
          <h1 className="text-[#0226BE] text-[16px] font-[500]">My Invoices</h1>
          <p className="text-[11px] text-[#666] mt-0.5">Recent billing history</p>
        </div>
        <button
          className="text-[#0226BE] text-[13px] font-[500] hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1"
          onClick={(e) => navigate('/invoice-billing')}
        >
          See all
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto containerScroll">
        <table className="w-full">
          <thead className="bg-gray-50/80 sticky top-0 z-10">
            <tr className="border-b border-[#E0E0E0]">
              {['Invoice', 'Completes', 'Amount', 'Status', 'Date']?.map((data, ind) => (
                <th
                  className="text-[#555] px-4 py-3 text-[11px] font-[600] text-left uppercase tracking-wide"
                  key={ind}
                >
                  {data}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoicesDataLoading ? (
              <tr>
                <td colSpan={5}>
                  <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-[240px]">
                    <div className="w-8 h-8 border-2 border-[#0226BE] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-[#666] text-[13px]">Loading invoices...</p>
                  </div>
                </td>
              </tr>
            ) : invoicesDataError ? (
              <tr>
                <td colSpan={5}>
                  <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-[240px]">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <p className="text-[#e12121] text-[13px] font-medium">Something went wrong!</p>
                  </div>
                </td>
              </tr>
            ) : invoicesData?.length ? (
              invoicesData?.map((data) => (
                <tr
                  className="border-b border-[#E0E0E0] hover:bg-blue-50/40 transition-colors cursor-pointer group"
                  key={data?._id}
                  onClick={(e) => navigate(`/invoice-billing/${data?._id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="text-[#333] font-[500] text-[13px] group-hover:text-[#0226BE] transition-colors">
                        {data?.invoiceNumber || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#666] font-[500] text-[13px]">
                    {data?.completes || 0}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[#333] font-[600] text-[13px]">
                      ${Number(data?.amount || 0)?.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {data?.paymentStats === 'paid' ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-[11px] font-[600] rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-[11px] font-[600] rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                        Unpaid
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#666] font-[400] text-[12px]">
                    {new Date(data?.invoiceDate)?.toLocaleDateString('en-in', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>
                  <NoData msg="No invoices found" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyInvoices;
